/**
 * RouteAnnouncer (Audit B-4 / docs/plans/a11y-deep-audit-2026-05-20.md)
 *
 * Mounts at the AppShell top level. On every route change, reads the
 * (newly-set) `document.title` and writes it into a visually-hidden
 * `aria-live="polite"` region so screen readers announce the page
 * change. Without this, SR users navigating via the back/forward
 * buttons or in-app links don't hear that the page actually changed.
 *
 * Implementation notes:
 *   - The announcer text is initialized empty and only becomes
 *     non-empty after the first navigation. This avoids announcing
 *     the document title on first paint (the SR already announces
 *     the page on initial load).
 *   - We defer the title read by one tick (`setTimeout(0)`) because
 *     the new page's `useEffect`/`useDocumentTitle` hook may not yet
 *     have run when the location changes — running synchronously
 *     would re-announce the previous page's title.
 *   - Pathname + search are both watched so query-only changes
 *     (e.g. /search?q=foo → /search?q=bar) still announce.
 *   - Visually hidden via the `.sr-only` class (defined in
 *     globals.css) — the same class ToastProvider's live region uses.
 */

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

export function RouteAnnouncer() {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    // Defer one tick so the new page's title hook has run.
    const timer = window.setTimeout(() => {
      const title = document.title.trim();
      if (title) {
        // Reset to empty first so SRs re-announce even when the title
        // is the same as the previous (e.g. tab → tab on the home
        // wall). Without the toggle, aria-live treats identical text
        // as a no-op.
        setAnnouncement("");
        window.setTimeout(() => setAnnouncement(title), 0);
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
      data-testid="route-announcer"
    >
      {announcement}
    </div>
  );
}
